import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../utils/seo';

export default function BlogPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [activeCategory, setActiveCategory] = useState('Tous');

  // Ajout du hook useInView manquant
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useSEO({
    title: 'Blog Voyage Elite | Conseils Ski, Stations Alpines & Transport Premium',
    description: 'Découvrez nos conseils pour votre séjour au ski : meilleures stations alpines, guide de voyage, conseils transport, actualités ski et montagne. Blog expert Voyage Elite.',
    keywords: 'blog ski Alpes, conseils stations ski, guide voyage montagne, actualités transport premium, conseils séjour ski',
    canonical: '/blog',
    ogTitle: 'Blog Voyage Elite - Conseils Ski & Stations Alpines',
    ogDescription: 'Conseils d\'experts pour votre séjour au ski dans les Alpes. Guides, actualités et bons plans.',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/blog#webpage`,
          url: `${siteUrl}/blog`,
          name: 'Blog Voyage Elite',
          description: 'Conseils et actualités ski, stations alpines et transport premium',
          inLanguage: 'fr-FR',
          isPartOf: {
            '@id': `${siteUrl}/#website`
          }
        },
        {
          '@type': 'Blog',
          '@id': `${siteUrl}/blog#blog`,
          url: `${siteUrl}/blog`,
          name: 'Blog Voyage Elite',
          description: 'Conseils d\'experts pour votre séjour au ski dans les Alpes',
          inLanguage: 'fr-FR',
          publisher: {
            '@type': 'Organization',
            name: 'Voyage Elite',
            url: siteUrl
          },
          blogPost: [
            {
              '@type': 'BlogPosting',
              headline: 'Les Meilleures Stations de Ski des 3 Vallées',
              description: 'Découvrez Courchevel, Méribel et Val Thorens, le plus grand domaine skiable du monde',
              datePublished: '2024-01-15',
              author: {
                '@type': 'Organization',
                name: 'Voyage Elite'
              }
            },
            {
              '@type': 'BlogPosting',
              headline: 'Guide Complet : Transfert Aéroport vers Station de Ski',
              description: 'Tous nos conseils pour un transfert réussi depuis l\'aéroport jusqu\'à votre station',
              datePublished: '2024-01-10',
              author: {
                '@type': 'Organization',
                name: 'Voyage Elite'
              }
            },
            {
              '@type': 'BlogPosting',
              headline: 'Top 10 des Stations de Ski près de Genève',
              description: 'Les meilleures destinations ski accessibles depuis l\'aéroport de Genève',
              datePublished: '2024-01-05',
              author: {
                '@type': 'Organization',
                name: 'Voyage Elite'
              }
            }
          ]
        },
        {
          '@type': 'ItemList',
          '@id': `${siteUrl}/blog#articlelist`,
          name: 'Articles du Blog Voyage Elite',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              url: `${siteUrl}/blog#article1`
            },
            {
              '@type': 'ListItem',
              position: 2,
              url: `${siteUrl}/blog#article2`
            },
            {
              '@type': 'ListItem',
              position: 3,
              url: `${siteUrl}/blog#article3`
            },
            {
              '@type': 'ListItem',
              position: 4,
              url: `${siteUrl}/blog#article4`
            },
            {
              '@type': 'ListItem',
              position: 5,
              url: `${siteUrl}/blog#article5`
            },
            {
              '@type': 'ListItem',
              position: 6,
              url: `${siteUrl}/blog#article6`
            }
          ]
        }
      ]
    }
  });

  const articles = [
    {
      id: 1,
      title: 'Comment Réserver un Chauffeur Privé pour l\'Aéroport ?',
      excerpt: 'Guide complet pour réserver votre chauffeur privé en 3 étapes simples. Découvrez nos conseils pour un transfert aéroport sans stress.',
      image: 'https://public.readdy.ai/ai/img_res/f0ca53b01c157483b280824f1b9c7eae.jpg',
      category: 'Réservation',
      date: '30 Janvier 2025',
      readTime: '6 min'
    },
    {
      id: 2,
      title: 'VTC Marseille : Notre Service de Chauffeur Privé Premium',
      excerpt: 'Découvrez notre service VTC haut de gamme à Marseille. Transport premium pour aéroport, gares et destinations dans toute la région PACA.',
      image: 'https://readdy.ai/api/search-image?query=black%20luxury%20mercedes%20sedan%20marseille%20old%20port%20vieux%20port%20mediterranean%20sea%20blue%20sky%20professional%20chauffeur%20service%20elegant%20premium%20transport%20provence%20french%20riviera%20sophisticated%20travel%20photography&width=800&height=600&seq=blog-vtc-marseille-service-102&orientation=landscape',
      category: 'Services',
      date: '29 Janvier 2025',
      readTime: '8 min'
    },
    {
      id: 3,
      title: 'Chauffeur Privé pour les Alpes : Guide Complet 2025',
      excerpt: 'Tout savoir sur les transferts privés vers les stations alpines. Tarifs, véhicules, conseils et itinéraires pour votre voyage en montagne.',
      image: 'https://readdy.ai/api/search-image?query=black%20mercedes%20luxury%20car%20driving%20on%20snowy%20alpine%20mountain%20road%20dramatic%20peaks%20winter%20landscape%20professional%20chauffeur%20service%20elegant%20premium%20mountain%20transfer%20blue%20sky%20majestic%20scenery%20sophisticated%20photography&width=800&height=600&seq=blog-alps-private-driver-103&orientation=landscape',
      category: 'Destinations',
      date: '28 Janvier 2025',
      readTime: '10 min'
    },
    {
      id: 4,
      title: 'Transfert Courchevel : Tarifs et Options de Transport 2025',
      excerpt: 'Prix détaillés pour rejoindre Courchevel depuis Genève, Lyon et Chambéry. Comparatif des options : VTC privé, navette partagée ou taxi.',
      image: 'https://readdy.ai/api/search-image?query=courchevel%201850%20luxury%20ski%20resort%20village%20snowy%20chalets%20elegant%20mercedes%20black%20car%20premium%20transfer%20service%20winter%20alpine%20destination%20sophisticated%20travel%20stunning%20mountain%20views%20professional%20photography&width=800&height=600&seq=blog-courchevel-transfer-prices-104&orientation=landscape',
      category: 'Tarifs',
      date: '27 Janvier 2025',
      readTime: '7 min'
    },
    {
      id: 5,
      title: 'Chauffeur Privé pour Mariage : Service Premium & Élégance',
      excerpt: 'Organisez le transport de vos invités pour votre mariage avec notre flotte Mercedes premium. Décoration, champagne et service sur-mesure.',
      image: 'https://readdy.ai/api/search-image?query=decorated%20luxury%20black%20mercedes%20wedding%20car%20white%20ribbons%20elegant%20floral%20decoration%20professional%20chauffeur%20in%20suit%20romantic%20wedding%20transportation%20premium%20service%20beautiful%20ceremony%20venue%20sophisticated%20photography&width=800&height=600&seq=blog-wedding-chauffeur-service-105&orientation=landscape',
      category: 'Événements',
      date: '26 Janvier 2025',
      readTime: '8 min'
    },
    {
      id: 6,
      title: 'Lyon - Val d\'Isère en VTC : Itinéraire, Durée et Conseils',
      excerpt: 'Trajet de 230 km en 3h15 par l\'A43. Découvrez l\'itinéraire optimal, les points d\'intérêt en route et nos conseils pour ce transfert alpin.',
      image: 'https://readdy.ai/api/search-image?query=highway%20route%20from%20lyon%20to%20val%20d%20isere%20french%20alps%20mountain%20scenery%20luxury%20black%20mercedes%20driving%20alpine%20landscape%20winter%20journey%20premium%20transfer%20beautiful%20mountain%20peaks%20blue%20sky%20sophisticated%20travel%20photography&width=800&height=600&seq=blog-lyon-valdisere-route-106&orientation=landscape',
      category: 'Itinéraires',
      date: '25 Janvier 2025',
      readTime: '9 min'
    },
    {
      id: 7,
      title: 'Mercedes Van Classe V : Le Véhicule Idéal pour Groupes',
      excerpt: 'Transport premium jusqu\'à 7 passagers avec notre Mercedes Classe V. Espace, confort et luxe pour vos déplacements en groupe vers les Alpes.',
      image: 'https://readdy.ai/api/search-image?query=black%20mercedes%20v%20class%20van%20luxury%20minivan%20spacious%20interior%20premium%20leather%20seats%20modern%20elegant%20design%20seven%20passenger%20capacity%20sophisticated%20business%20transport%20high-end%20group%20travel%20professional%20photography&width=800&height=600&seq=blog-mercedes-van-group-107&orientation=landscape',
      category: 'Véhicules',
      date: '24 Janvier 2025',
      readTime: '6 min'
    },
    {
      id: 8,
      title: 'Services Voyage Elite : Transport Premium & Conciergerie de Luxe',
      excerpt: 'Découvrez l\'ensemble de nos services : transferts aéroport, mise à disposition, événements professionnels et conciergerie personnalisée.',
      image: 'https://readdy.ai/api/search-image?query=luxury%20concierge%20service%20premium%20hospitality%20elegant%20reception%20professional%20staff%20modern%20sophisticated%20environment%20high-end%20customer%20service%20business%20class%20experience%20refined%20atmosphere%20contemporary%20design&width=800&height=600&seq=blog-voyage-elite-services-108&orientation=landscape',
      category: 'Services',
      date: '23 Janvier 2025',
      readTime: '8 min'
    },
    {
      id: 9,
      title: 'Transport Privé pour Groupe : Van Mercedes et Solutions Premium',
      excerpt: 'Organisez le déplacement de votre groupe avec nos Mercedes Classe V. Idéal pour séminaires, mariages, voyages d\'affaires et groupes d\'amis.',
      image: 'https://readdy.ai/api/search-image?query=business%20group%20entering%20luxury%20black%20mercedes%20van%20professional%20travelers%20modern%20corporate%20transport%20elegant%20premium%20minivan%20sophisticated%20business%20trip%20team%20travel%20airport%20transfer%20high-end%20service%20photography&width=800&height=600&seq=blog-group-transport-solutions-109&orientation=landscape',
      category: 'Groupes',
      date: '22 Janvier 2025',
      readTime: '7 min'
    },
    {
      id: 10,
      title: 'Chauffeur Privé Gares de Lyon : Part-Dieu & Saint-Exupéry',
      excerpt: 'Service VTC premium pour vos arrivées et départs en gare. Transfert depuis Lyon Part-Dieu, Perrache et la gare TGV Saint-Exupéry.',
      image: 'https://readdy.ai/api/search-image?query=lyon%20part%20dieu%20train%20station%20modern%20tgv%20terminal%20black%20luxury%20mercedes%20professional%20chauffeur%20service%20elegant%20business%20travel%20premium%20transport%20french%20railway%20sophisticated%20urban%20photography&width=800&height=600&seq=blog-lyon-train-stations-chauffeur-110&orientation=landscape',
      category: 'Gares',
      date: '21 Janvier 2025',
      readTime: '6 min'
    },
    {
      id: 11,
      title: 'Les Avantages d\'un Chauffeur Privé vs VTC Classique',
      excerpt: 'Comparaison détaillée entre chauffeur privé premium et VTC standard. Comprendre la différence de service, confort et professionnalisme.',
      image: 'https://readdy.ai/api/search-image?query=split%20screen%20comparison%20luxury%20premium%20chauffeur%20versus%20standard%20taxi%20service%20professional%20elegant%20mercedes%20black%20car%20sophisticated%20business%20class%20transport%20high-end%20service%20difference%20quality%20upgrade%20contemporary%20photography&width=800&height=600&seq=blog-private-driver-advantages-111&orientation=landscape',
      category: 'Conseils',
      date: '20 Janvier 2025',
      readTime: '8 min'
    },
    {
      id: 12,
      title: 'Choisir son Service de VTC Haut de Gamme : 10 Critères Essentiels',
      excerpt: 'Guide pour sélectionner le meilleur service de transport premium. Véhicules, chauffeurs, ponctualité, tarifs : nos conseils d\'expert.',
      image: 'https://readdy.ai/api/search-image?query=checklist%20evaluation%20criteria%20luxury%20vehicle%20selection%20professional%20quality%20standards%20premium%20transport%20service%20modern%20elegant%20business%20environment%20sophisticated%20decision%20making%20contemporary%20design%20photography&width=800&height=600&seq=blog-choose-luxury-vtc-service-112&orientation=landscape',
      category: 'Conseils',
      date: '19 Janvier 2025',
      readTime: '9 min'
    },
    {
      id: 13,
      title: 'Réservation Multi-Jours : Chauffeur Privé à Disposition',
      excerpt: 'Service de mise à disposition pour plusieurs jours. Idéal pour séminaires, tournées commerciales ou séjours touristiques dans les Alpes.',
      image: 'https://readdy.ai/api/search-image?query=professional%20chauffeur%20waiting%20beside%20luxury%20black%20mercedes%20multi-day%20service%20dedicated%20driver%20elegant%20premium%20transport%20business%20trip%20extended%20service%20sophisticated%20alpine%20scenery%20professional%20photography&width=800&height=600&seq=blog-multi-day-chauffeur-booking-113&orientation=landscape',
      category: 'Réservation',
      date: '18 Janvier 2025',
      readTime: '7 min'
    },
    {
      id: 14,
      title: 'Flotte Voyage Elite : Mercedes Classe E, Classe S et Van Classe V',
      excerpt: 'Présentation détaillée de nos véhicules premium. Caractéristiques, capacités et équipements de chaque modèle Mercedes de notre flotte.',
      image: 'https://readdy.ai/api/search-image?query=mercedes%20fleet%20showcase%20three%20luxury%20vehicles%20class%20e%20class%20s%20and%20v%20class%20van%20black%20elegant%20premium%20cars%20modern%20showroom%20sophisticated%20automotive%20presentation%20high-end%20transport%20professional%20photography%20white%20background&width=800&height=600&seq=blog-voyage-elite-fleet-details-114&orientation=landscape',
      category: 'Véhicules',
      date: '17 Janvier 2025',
      readTime: '8 min'
    },
    {
      id: 15,
      title: 'Déroulement d\'un Transfert Aéroport avec Voyage Elite',
      excerpt: 'Découvrez pas à pas comment se déroule votre transfert : réservation, suivi de vol, accueil personnalisé et trajet jusqu\'à destination.',
      image: 'https://readdy.ai/api/search-image?query=airport%20terminal%20arrival%20professional%20chauffeur%20holding%20welcome%20sign%20passenger%20greeting%20luxury%20service%20black%20mercedes%20waiting%20premium%20transfer%20elegant%20hospitality%20sophisticated%20business%20travel%20contemporary%20photography&width=800&height=600&seq=blog-airport-transfer-process-115&orientation=landscape',
      category: 'Services',
      date: '16 Janvier 2025',
      readTime: '7 min'
    },
    {
      id: 16,
      title: 'Voyage d\'Affaires : Transport Professionnel Premium',
      excerpt: 'Solutions de transport adaptées aux déplacements professionnels. Discrétion, ponctualité et services dédiés aux executives et cadres dirigeants.',
      image: 'https://readdy.ai/api/search-image?query=business%20executive%20in%20suit%20working%20on%20laptop%20inside%20luxury%20mercedes%20black%20car%20professional%20corporate%20travel%20premium%20chauffeur%20service%20sophisticated%20business%20trip%20elegant%20interior%20modern%20technology%20contemporary%20photography&width=800&height=600&seq=blog-business-travel-executive-transport-116&orientation=landscape',
      category: 'Professionnel',
      date: '15 Janvier 2025',
      readTime: '8 min'
    },
    {
      id: 17,
      title: 'Politique d\'Annulation Voyage Elite : Flexibilité & Transparence',
      excerpt: 'Tout savoir sur nos conditions d\'annulation et de modification. Garanties, remboursements et options flexibles pour vos réservations.',
      image: 'https://readdy.ai/api/search-image?query=calendar%20scheduling%20flexibility%20cancellation%20policy%20document%20modern%20elegant%20design%20professional%20business%20terms%20transparent%20conditions%20premium%20service%20customer-friendly%20contemporary%20minimalist%20photography&width=800&height=600&seq=blog-cancellation-policy-flexibility-117&orientation=landscape',
      category: 'Informations',
      date: '14 Janvier 2025',
      readTime: '5 min'
    },
    {
      id: 18,
      title: 'Services Inclus dans un Transfert Premium Voyage Elite',
      excerpt: 'Découvrez tous les services compris dans nos tarifs : bagages ski, WiFi, eau, suivi de vol, sièges enfants et assistance 24/7.',
      image: 'https://readdy.ai/api/search-image?query=luxury%20car%20interior%20premium%20amenities%20water%20bottles%20wifi%20signal%20ski%20equipment%20elegant%20mercedes%20services%20included%20sophisticated%20travel%20comfort%20high-end%20features%20business%20class%20contemporary%20photography&width=800&height=600&seq=blog-premium-services-included-118&orientation=landscape',
      category: 'Services',
      date: '13 Janvier 2025',
      readTime: '6 min'
    },
    {
      id: 19,
      title: 'Aéroports Genève & Lyon : Nos Points de Prise en Charge',
      excerpt: 'Informations pratiques sur nos services depuis les aéroports de Genève (GVA) et Lyon Saint-Exupéry (LYS). Zones de rencontre et procédures.',
      image: 'https://readdy.ai/api/search-image?query=geneva%20and%20lyon%20airports%20terminals%20split%20view%20modern%20architecture%20professional%20chauffeur%20service%20pickup%20points%20luxury%20black%20mercedes%20premium%20transport%20international%20airports%20sophisticated%20travel%20photography&width=800&height=600&seq=blog-geneva-lyon-airports-pickup-119&orientation=landscape',
      category: 'Aéroports',
      date: '12 Janvier 2025',
      readTime: '7 min'
    },
    {
      id: 20,
      title: 'Véhicule de Prestige : Mercedes Premium pour Vos Déplacements',
      excerpt: 'Pourquoi choisir Mercedes pour vos transferts ? Confort, sécurité, technologie et image de marque au service de votre voyage.',
      image: 'https://readdy.ai/api/search-image?query=pristine%20black%20mercedes%20luxury%20sedan%20front%20view%20elegant%20premium%20vehicle%20sophisticated%20automotive%20design%20high-end%20prestige%20car%20modern%20technology%20professional%20transport%20excellence%20contemporary%20photography%20studio%20lighting&width=800&height=600&seq=blog-mercedes-prestige-vehicles-120&orientation=landscape',
      category: 'Véhicules',
      date: '11 Janvier 2025',
      readTime: '6 min'
    },
    {
      id: 21,
      title: 'Accompagnement Personnalisé : L\'Excellence du Service Voyage Elite',
      excerpt: 'Notre approche du service client : écoute, anticipation, discrétion et professionnalisme. Ce qui fait la différence Voyage Elite.',
      image: 'https://readdy.ai/api/search-image?query=professional%20chauffeur%20assisting%20elegant%20passenger%20with%20luggage%20personalized%20service%20luxury%20hospitality%20premium%20customer%20care%20sophisticated%20attention%20detail%20high-end%20transport%20white%20glove%20service%20contemporary%20photography&width=800&height=600&seq=blog-personalized-service-excellence-121&orientation=landscape',
      category: 'Services',
      date: '10 Janvier 2025',
      readTime: '7 min'
    },
    {
      id: 22,
      title: 'Navette Station de Ski : Solutions Premium pour la Montagne',
      excerpt: 'Toutes nos destinations alpines desservies : Chamonix, Courchevel, Val d\'Isère, Méribel. Tarifs et options de transport vers les stations.',
      image: 'https://readdy.ai/api/search-image?query=black%20mercedes%20driving%20toward%20luxury%20ski%20resort%20village%20snowy%20alpine%20mountains%20winter%20landscape%20premium%20shuttle%20service%20elegant%20mountain%20transfer%20sophisticated%20destination%20travel%20professional%20photography&width=800&height=600&seq=blog-ski-station-shuttle-premium-122&orientation=landscape',
      category: 'Destinations',
      date: '9 Janvier 2025',
      readTime: '9 min'
    },
    {
      id: 23,
      title: 'Transport Exécutif : Service VIP pour Dirigeants et Executives',
      excerpt: 'Solutions de mobilité haut de gamme pour dirigeants d\'entreprise. Confidentialité, efficacité et service irréprochable garantis.',
      image: 'https://readdy.ai/api/search-image?query=business%20executive%20ceo%20entering%20luxury%20black%20mercedes%20vip%20treatment%20professional%20chauffeur%20service%20high-end%20corporate%20transport%20sophisticated%20business%20leader%20elegant%20premium%20mobility%20contemporary%20photography&width=800&height=600&seq=blog-executive-vip-transport-123&orientation=landscape',
      category: 'Professionnel',
      date: '8 Janvier 2025',
      readTime: '8 min'
    },
    {
      id: 24,
      title: 'Mobilité Haut de Gamme : Redéfinir l\'Expérience du Transport',
      excerpt: 'Comment Voyage Elite transforme le transport en expérience premium. Innovation, excellence et attention aux détails à chaque trajet.',
      image: 'https://readdy.ai/api/search-image?query=futuristic%20luxury%20transport%20concept%20elegant%20black%20mercedes%20modern%20premium%20mobility%20high-end%20travel%20experience%20sophisticated%20innovation%20contemporary%20design%20refined%20service%20excellence%20professional%20photography&width=800&height=600&seq=blog-premium-mobility-redefined-124&orientation=landscape',
      category: 'Vision',
      date: '7 Janvier 2025',
      readTime: '7 min'
    }
  ];

  const categories = ['Tous', 'Réservation', 'Services', 'Destinations', 'Tarifs', 'Événements', 'Conseils', 'Véhicules', 'Professionnel', 'Groupes', 'Gares', 'Aéroports', 'Itinéraires', 'Informations', 'Vision'];

  const filteredArticles = activeCategory === 'Tous' 
    ? articles 
    : articles.filter(article => article.category === activeCategory);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main>
        <section className="relative h-[60vh] w-full overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://readdy.ai/api/search-image?query=elegant%20alpine%20mountain%20landscape%20snowy%20peaks%20luxury%20travel%20blog%20header%20modern%20minimalist%20design%20premium%20winter%20atmosphere%20blue%20white%20tones%20sophisticated%20photography%20clean%20aesthetic&width=1920&height=800&seq=blog-hero-header-001&orientation=landscape')`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/40"></div>

          <div className="relative h-full flex flex-col items-center justify-center px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20"
            >
              <i className="ri-article-line text-white text-base" aria-hidden="true"></i>
              <span className="text-white font-inter text-sm">Blog & Actualités</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="font-playfair text-white text-6xl lg:text-7xl font-bold mb-6"
            >
              <strong>NOTRE BLOG</strong>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-slate-200 font-inter text-lg max-w-2xl"
            >
              Conseils, destinations et actualités du <strong>transport premium</strong> vers les Alpes
            </motion.p>
          </div>
        </section>

        <section ref={ref} className="py-20 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              className="flex flex-wrap gap-3 mb-12 justify-center"
            >
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-6 py-2 rounded-full font-inter text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    cat === activeCategory
                      ? 'bg-night-blue text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  aria-label={`Filtrer par catégorie ${cat}`}
                >
                  {cat}
                </button>
              ))}
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 50 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl overflow-hidden border border-slate-200 hover:shadow-2xl transition-all cursor-pointer group"
                >
                  <div className="relative h-64 w-full overflow-hidden">
                    <img
                      src={article.image}
                      alt={`${article.title} - Guide transfert chauffeur privé VTC`}
                      title={article.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-4 py-1 bg-white/90 backdrop-blur-sm rounded-full font-inter text-xs font-medium text-night-blue">
                        {article.category}
                      </span>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-4 mb-3 text-slate-500 font-inter text-xs">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line" aria-hidden="true"></i>
                        <time dateTime={article.date}>{article.date}</time>
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line" aria-hidden="true"></i>
                        {article.readTime}
                      </span>
                    </div>

                    <h3 className="font-playfair text-night-blue text-xl font-bold mb-3 group-hover:text-alpine-gold transition-colors">
                      <strong>{article.title}</strong>
                    </h3>

                    <p className="text-slate-600 font-inter text-sm leading-relaxed mb-4">
                      {article.excerpt}
                    </p>

                    <button className="flex items-center gap-2 text-night-blue font-inter text-sm font-medium group-hover:text-alpine-gold transition-colors whitespace-nowrap cursor-pointer">
                      Lire l'article
                      <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                    </button>
                  </div>
                </motion.article>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-16">
                <i className="ri-article-line text-slate-300 text-6xl mb-4" aria-hidden="true"></i>
                <p className="text-slate-500 font-inter text-lg">
                  Aucun article dans cette catégorie pour le moment
                </p>
              </div>
            )}

            {filteredArticles.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.6 }}
                className="text-center mt-16"
              >
                <button className="px-10 py-4 bg-slate-100 text-night-blue rounded-full font-inter font-medium hover:bg-night-blue hover:text-white transition-all whitespace-nowrap cursor-pointer">
                  Charger Plus d'Articles
                </button>
              </motion.div>
            )}
          </div>
        </section>

        <section className="py-20 px-6 lg:px-12 bg-gradient-to-br from-night-blue to-slate-900">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <i className="ri-mail-line text-white text-5xl mb-6" aria-hidden="true"></i>
              <h2 className="font-playfair text-white text-4xl lg:text-5xl font-bold mb-4">
                <strong>Restez Informé</strong>
              </h2>
              <p className="text-slate-300 font-inter text-lg mb-8">
                Recevez nos derniers articles et <strong>offres exclusives</strong> directement dans votre boîte mail
              </p>

              <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
                <input
                  type="email"
                  placeholder="Votre adresse email"
                  aria-label="Votre adresse email pour la newsletter"
                  className="flex-1 px-6 py-4 rounded-full font-inter text-base border-2 border-white/20 bg-white/10 text-white placeholder-white/60 focus:outline-none focus:border-alpine-gold transition-colors"
                />
                <button className="px-8 py-4 bg-alpine-gold text-night-blue rounded-full font-inter font-medium hover:bg-white transition-all whitespace-nowrap cursor-pointer">
                  S'abonner
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}